import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Box,
  Link,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext.js';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword } = useAuth();
  
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  useEffect(() => {
    // Extract token from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get('token');
    if (tokenParam) {
      // Decode the token in case it was URL encoded
      const decodedToken = decodeURIComponent(tokenParam);
      setToken(decodedToken);
      console.log('Token extracted from URL:', decodedToken);
    }
  }, [location]);
  
  // Debug token value changes
  useEffect(() => {
    console.log('Current token state:', token);
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Validate token is present
    if (!token || token.trim() === '') {
      setError('Reset token is missing. Please use the link from your email or enter the token manually.');
      setLoading(false);
      return;
    }
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    try {
      console.log('Submitting password reset with token:', token);
      // Send both newPassword and confirmPassword to match backend expectations
      await resetPassword(token, newPassword, confirmPassword);
      setSuccess(true);
      setTimeout(() => {
        // If we received a token, navigate to home page, otherwise to login
        const token = localStorage.getItem('token');
        if (token) {
          navigate('/');
        } else {
          navigate('/login');
        }
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.response?.status === 0) {
        setError('Unable to connect to the server. Please try again later.');
      } else {
        setError(error.response?.data?.message || 'Failed to reset password');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Reset Password
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Your password has been reset successfully. You will be automatically logged in if possible, or redirected to the login page.
        </Alert>
      )}
      
      <TextField
        required
        fullWidth
        id="token"
        label="Reset Token"
        name="token"
        value={token}
        onChange={(e) => setToken(e.target.value)}
        disabled={loading || success}
        sx={{ display: token ? 'none' : 'block' }} // Hide if token is already provided in URL
        helperText="Please enter the token from your password reset email"
      />
      
      <TextField
        required
        fullWidth
        name="newPassword"
        label="New Password"
        type={showPassword ? 'text' : 'password'}
        id="newPassword"
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        disabled={loading || success}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <TextField
        required
        fullWidth
        name="confirmPassword"
        label="Confirm Password"
        type={showPassword ? 'text' : 'password'}
        id="confirmPassword"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        disabled={loading || success}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        sx={{ mt: 3, mb: 2 }}
        disabled={loading || success}
      >
        {loading ? <CircularProgress size={24} /> : 'Reset Password'}
      </Button>
      
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Link component={RouterLink} to="/login" variant="body2">
          Back to login
        </Link>
      </Box>
    </Box>
  );
};

export default ResetPassword;
